import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import {
  usePublicChangelog,
  type PublicChangelogCard,
} from "@/api/queries/publicChangelog";
import { ChangelogCover } from "@/components/changelog/ChangelogBlocks";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { changelogAssetUrl } from "@/lib/changelogAsset";
import { useSeo } from "@/lib/seo";

/**
 * Přehled novinek platformy — apex (``forgmatic.com/changelog``).
 *
 * Dvě úrovně vedle sebe, jako na blozích, které tenhle formát dělají
 * dobře: **článek** dostane velkou kartu s přebalem, **výčet oprav**
 * jen kompaktní řádek. Bez toho dvojího vzniká tlak nafouknout tři
 * opravy do velkého článku — a index se čte jako reklama na nic.
 *
 * Tenant shopy tuhle stránku nemají a jejich sitemapa ji nelistuje;
 * changelog vydává platforma o sobě.
 */
export default function Changelog(): JSX.Element {
  const { t } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const [module, setModule] = useState<string | null>(null);
  const index = usePublicChangelog(lang, module);

  useSeo({
    title: t("changelog.seoTitle", "Novinky · Forgmatic"),
    description: t(
      "changelog.seoDescription",
      "Co na platformě přibylo — přehled vydání.",
    ),
    path: "/changelog",
    lang,
  });

  const items = index.data?.items ?? [];
  const modules = index.data?.modules ?? [];

  return (
    <main className="mx-auto max-w-4xl px-7 py-10" data-testid="changelog-page">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-ink">
          {t("changelog.heading", "Novinky")}
        </h1>
        <p className="mt-2 max-w-2xl text-ink-2">
          {t(
            "changelog.lead",
            "Co jsme nasadili a co to znamená pro vaši práci.",
          )}
        </p>
      </header>

      {modules.length > 0 && (
        <nav className="mb-6 flex flex-wrap gap-2" data-testid="changelog-filters">
          <FilterChip
            label={t("changelog.filterAll", "Vše")}
            active={module === null}
            onClick={() => setModule(null)}
            testId="changelog-filter-all"
          />
          {modules.map((entry) => (
            <FilterChip
              key={entry.key}
              label={entry.label}
              active={module === entry.key}
              onClick={() => setModule(entry.key)}
              testId={`changelog-filter-${entry.key}`}
            />
          ))}
        </nav>
      )}

      {index.isPending && (
        <p className="text-sm text-ink-3">{t("changelog.loading", "Načítám…")}</p>
      )}
      {index.error != null && (
        <p className="text-sm text-danger">
          {t("changelog.error", "Novinky se nepodařilo načíst.")}
        </p>
      )}
      {!index.isPending && index.error == null && items.length === 0 && (
        <p className="text-ink-3" data-testid="changelog-empty">
          {t("changelog.empty", "Zatím tu nic není.")}
        </p>
      )}

      <ul className="space-y-6" data-testid="changelog-index">
        {items.map((item) =>
          item.tier === "article" ? (
            <ArticleCard key={item.slug} item={item} lang={lang} />
          ) : (
            <PatchNotesRow key={item.slug} item={item} lang={lang} />
          ),
        )}
      </ul>
    </main>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-sm ${
        active
          ? "border-accent bg-accent-bg text-accent-ink"
          : "border-border bg-surface text-ink-2"
      }`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function ReleaseMeta({ item }: { item: PublicChangelogCard }): JSX.Element {
  const { i18n } = useTranslation();
  const date = item.released_at
    ? new Date(item.released_at).toLocaleDateString(i18n.language)
    : null;
  return (
    <span className="text-xs text-ink-3">
      {[item.version_tag, date].filter(Boolean).join(" · ")}
    </span>
  );
}

function ArticleCard({
  item,
  lang,
}: {
  item: PublicChangelogCard;
  lang: string;
}): JSX.Element {
  const [heroFailed, setHeroFailed] = useState(false);
  return (
    <li>
      <Link
        to={`/${lang}/changelog/${item.slug}`}
        className="block rounded-lg border border-border bg-surface p-5 hover:border-ink-3"
        data-testid={`changelog-card-${item.slug}`}
      >
        {/* Bez vlastního vizuálu nastoupí značkový přebal — index nikdy
            nemá díru, což je i důvod, proč tu nic není povinné. */}
        {item.hero_asset_key && !heroFailed ? (
          <img
            className="mb-4 w-full rounded-lg border border-border"
            src={changelogAssetUrl(item.hero_asset_key)}
            alt=""
            loading="lazy"
            onError={() => setHeroFailed(true)}
            data-testid="changelog-card-hero"
          />
        ) : (
          <ChangelogCover
            title={item.title}
            versionTag={item.version_tag}
            className="mb-4 h-28"
          />
        )}
        <ReleaseMeta item={item} />
        <h2 className="mt-1 text-xl font-semibold text-ink">{item.title}</h2>
        {item.excerpt != null && (
          <p className="mt-2 text-ink-2">{item.excerpt}</p>
        )}
      </Link>
    </li>
  );
}

function PatchNotesRow({
  item,
  lang,
}: {
  item: PublicChangelogCard;
  lang: string;
}): JSX.Element {
  return (
    <li>
      <Link
        to={`/${lang}/changelog/${item.slug}`}
        className="flex items-baseline justify-between gap-3 rounded-md border border-border bg-surface-2 px-4 py-3 hover:border-ink-3"
        data-testid={`changelog-row-${item.slug}`}
      >
        <span className="text-sm text-ink">{item.title}</span>
        <ReleaseMeta item={item} />
      </Link>
    </li>
  );
}
